import React from "react"
import { Input, Button, DatePicker } from "antd"
import { SearchOutlined, DownOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import CategorySelect from "components/common/CategorySelect"
import styles from "./index.module.css"

const CrewFilters = ({
  search,
  onSearchChange,
  selectedRoles,
  onRoleChange,
  roleOptions,
  selectedStatuses,
  onStatusChange,
  statusOptions,
  datePicker
}) => {
  const { t } = useTranslation()
  const { datePickerOpen, datePickerValue, currentDisplayDate, handleOpenChange, handleCalendarChange, handleChange, handleDone, handleToggle } =
    datePicker

  return (
    <div className={styles.filtersRow}>
      <Input
        id="search"
        type="text"
        placeholder={t("listings.tabs.resources.crew_members_placeholder") || "Search for Crew Members..."}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className={styles.searchInput}
        prefix={<SearchOutlined aria-hidden="true" />}
        allowClear
        aria-label={t("clients.search_clients") || "Search"}
      />
      <CategorySelect
        value={selectedRoles}
        onChange={onRoleChange}
        options={roleOptions}
        placeholder={selectedRoles.length > 0 ? t("Roles") : "All Roles"}
        dropdownClassName={styles.dropdownButton}
        t={t}
      />
      <CategorySelect
        value={selectedStatuses}
        onChange={onStatusChange}
        options={statusOptions}
        placeholder={selectedStatuses.length > 0 ? t("Statuses") : "All Statuses"}
        dropdownClassName={styles.dropdownButton}
      />
      <div className={styles.datePickerWrapper}>
        <DatePicker.RangePicker
          open={datePickerOpen}
          onOpenChange={handleOpenChange}
          value={datePickerValue}
          allowClear={false}
          className={`${styles.dropdownButton} ${styles.hiddenDateInput}`}
          format={() => currentDisplayDate.format("YYYY-MM-DD")}
          onCalendarChange={handleCalendarChange}
          onChange={handleChange}
          renderExtraFooter={() => (
            <div style={{ textAlign: "right" }}>
              <Button type="primary" size="small" onClick={handleDone} disabled={!datePicker.pendingDate}>
                Done
              </Button>
            </div>
          )}
        />
        <Button
          type="text"
          className={styles.dateDisplay}
          aria-label={`Select availability date, currently ${currentDisplayDate.format("MMMM D, YYYY")}`}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              handleToggle()
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={datePickerOpen}
          style={{
            display: "flex",
            alignItems: "center",
            background: "#fff",
            border: "1px solid #e5e6e8",
            boxShadow: "0 2px 8px 0 rgba(16, 30, 54, 0.04)",
            borderRadius: 8,
            padding: "8px 16px",
            cursor: "pointer",
            width: "100%",
            minHeight: 40,
            textAlign: "left",
            gap: 8
          }}
        >
          <span className={styles.dateLabel} style={{ color: "#bdbdbd", fontWeight: 400, fontSize: 16 }}>
            Available on:
          </span>
          <span className={styles.dateValue} style={{ color: "#222", fontWeight: 400, fontSize: 16, marginLeft: 4 }}>
            {currentDisplayDate.format("MMMM D, YYYY")}
          </span>
          <DownOutlined className={styles.dateCaret} style={{ color: "#bdbdbd", fontSize: 18, marginLeft: "auto" }} />
        </Button>
      </div>
    </div>
  )
}

CrewFilters.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  selectedRoles: PropTypes.array.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  roleOptions: PropTypes.array.isRequired,
  selectedStatuses: PropTypes.array.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  statusOptions: PropTypes.array.isRequired,
  datePicker: PropTypes.shape({
    datePickerOpen: PropTypes.bool.isRequired,
    datePickerValue: PropTypes.array.isRequired,
    currentDisplayDate: PropTypes.object.isRequired,
    pendingDate: PropTypes.object,
    handleOpenChange: PropTypes.func.isRequired,
    handleCalendarChange: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleDone: PropTypes.func.isRequired,
    handleToggle: PropTypes.func.isRequired
  }).isRequired
}

export default React.memo(CrewFilters)
